// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import crest from "../../dmxHelper/assets/wrpfarg-logo.webp";
import FitLine from "../../live/FitLine";
import type { LabSponsor } from "../labSponsors";
import SponsorRotator from "../../live/SponsorRotator";
import { AttemptCells, bestLabel, ClockBox, JudgeLightsBox, RecordTag, recordStyle, totalLabel } from "../../streaming/broadcast/overlayParts";
import type { OverlayView } from "../../streaming/broadcast/overlayView";
import { LIFT_LABELS } from "../../streaming/broadcast/overlayView";

import styles from "./OverlayPlaca.module.scss";

interface Props {
  view: OverlayView;
  sponsors: ReadonlyArray<LabSponsor>;
}

// Dirección A: placa horizontal. El peso en barra manda; los movimientos se apilan como en la pantalla de espectador.
const OverlayPlaca = ({ view, sponsors }: Props) => {
  const athlete = view.athlete;
  const barCaption = view.result === "good" ? "Válido" : view.result === "noLift" ? "Nulo" : athlete?.attemptLabel;
  return (
    <div className={styles.root}>
      <header className={styles.bug}>
        <div className={styles.bugCrest}>
          <img src={crest} alt="WRPF Argentina" />
        </div>
        <div className={styles.bugText}>
          <strong>{view.tournamentName}</strong>
          <span>{view.sessionLabel}</span>
        </div>
      </header>

      <JudgeLightsBox lights={view.lights} className={styles.lights} />
      <ClockBox seconds={view.clockSeconds} running={view.clockRunning} className={styles.clock} />

      {athlete !== null ? (
        <section key={athlete.entryKey} className={styles.lowerThird} aria-label="Atleta en tarima">
          {athlete.record !== null ? (
            <div className={styles.recordBand} style={recordStyle(athlete.record.color, athlete.record.textColor)}>
              Intento de {athlete.record.label.toLowerCase()}
            </div>
          ) : null}
          <div className={styles.slab}>
            <div className={styles.bar} data-result={view.result ?? undefined}>
              <span className={styles.barCaption}>{barCaption}</span>
              <span className={styles.barWeight}>
                {athlete.weightLabel}
                <small>kg</small>
              </span>
            </div>

            <div className={styles.identity}>
              <div className={styles.name}>
                <FitLine maxFontSize={60} minFontSize={26}>
                  {athlete.name}
                </FitLine>
              </div>
              <ul className={styles.details}>
                {athlete.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
              <ul className={styles.origin}>
                {athlete.origin.map((value) => (
                  <li key={value}>{value}</li>
                ))}
              </ul>
            </div>

            <div className={styles.lifts} data-rows={athlete.progress.rows.length}>
              {athlete.progress.rows.map((row) => (
                <div key={row.lift} className={styles.liftRow} data-current={row.current ? "true" : undefined}>
                  <span className={styles.liftLabel}>{LIFT_LABELS[row.lift]}</span>
                  {row.current ? (
                    <AttemptCells row={row} attemptNumber={athlete.attemptNumber} className={styles.cells} />
                  ) : (
                    <span className={styles.best} data-state={row.state}>
                      {bestLabel(row)}
                      <RecordTag row={row} className={styles.recordTag} />
                    </span>
                  )}
                </div>
              ))}
            </div>

            {athlete.progress.showTotal ? (
              <div className={styles.total} data-bombed={athlete.progress.bombed ? "true" : undefined}>
                <span>{athlete.progress.bombed ? "Sin total" : "Total"}</span>
                <strong>{totalLabel(athlete.progress)}</strong>
              </div>
            ) : null}
          </div>
        </section>
      ) : view.waitingForNext ? (
        <section className={styles.waiting} aria-label="Atleta en tarima">
          Esperando al siguiente atleta
        </section>
      ) : null}

      <SponsorRotator sponsors={sponsors} className={styles.sponsors} tileClassName={styles.sponsorTile} />
    </div>
  );
};

export default OverlayPlaca;
